import { alpha } from '@mui/material/styles';
import { brand, colorSchemes, gray, green, orange, red } from '@theme/primitives';

/**
 * The card's colour, written out because the theme's carries `!important` and cannot be mixed.
 * Every cell is painted with it, or with a mix of it, so a sticky cell covers what scrolls
 * under it.
 */
const surface = '#15171C';
const hoverSurface = `color-mix(in srgb, ${gray[600]} 22%, ${surface})`;
// A picked row is tinted lightly and marked by a line at its start; the checkbox says the rest.
const selectedSurface = `color-mix(in srgb, ${brand[400]} 8%, ${surface})`;
const selectedHoverSurface = `color-mix(in srgb, ${brand[400]} 12%, ${surface})`;

/**
 * The table's three shades of text: what a row is about (its name, a sorted column), what it
 * says, and what stands back (column names, a username, what is not set, the row's tools).
 * Nothing in a table is pure white.
 */
export const tableText = {
  primary: 'hsl(225, 22%, 94%)',
  secondary: gray[400],
  tertiary: 'hsl(222, 11%, 54%)',
};

/** The lines between rows, quieter than the card's edge and the line under the column names. */
const rowLine = 'hsl(220, 16%, 15%)';

/**
 * The well the bar's fields sit in (the segments, the search, the page size) and its edge, and
 * what stands raised out of it (the segment that is on, a bulk action), each with its shade under
 * the pointer. The theme's grey steps from 25% to 6% lightness in one, so these between are the
 * table's own.
 */
const segmentWell = 'hsl(222, 16%, 7%)';
const fieldEdge = 'hsl(222, 13%, 19%)';
const fieldEdgeHover = 'hsl(222, 12%, 26%)';
const segmentRaised = 'hsl(222, 13%, 17%)';
const raisedHover = 'hsl(222, 12%, 23%)';

/** Roomy enough for a name over its username. A cell that reacts to the pointer fills it. */
export const rowHeight = 56;

/** The checkbox column, which the first sticky column stands right of. */
export const selectColumnWidth = 44;

/** A column without a width of its own takes what the others leave, and never less than this. */
export const flexibleColumnWidth = 240;

export const cardStyles = {
  width: '100%',
  overflow: 'hidden',
  backgroundColor: surface,
};

/**
 * The card is as tall as its rows and the page scrolls; the rows scroll inside it only sideways,
 * where the columns are wider than the card.
 */
export const scrollStyles = {
  position: 'relative',
  overflowX: 'auto',
  // A line where the row's name stands over the columns scrolled under it (`dt-scrolled`, set
  // while the table is scrolled sideways). Drawn inside the cell, so nothing moves by a pixel.
  '&.dt-scrolled .dt-sticky-start:not(.dt-select)': {
    boxShadow: `inset -1px 0 0 ${colorSchemes.divider}`,
  },
  // And one where the actions stand over the columns past the right edge (`dt-more`, set while
  // there are any), so that a table wider than its card says it goes on.
  '&.dt-more .dt-sticky-end': {
    boxShadow: `inset 1px 0 0 ${colorSchemes.divider}`,
  },
};

export const tableStyles = {
  width: '100%',
  // Laid out by the column widths and not by what the rows hold, so that the columns stay put
  // while a search, a segment or a page changes the rows.
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
  fontVariantNumeric: 'tabular-nums',

  '& th, & td': {
    px: 1.5,
    py: 0,
    textAlign: 'start',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    backgroundColor: surface,
    borderBottom: 1,
    borderColor: rowLine,
  },
  '& th:first-of-type, & td:first-of-type': { pl: 2 },
  '& th:last-of-type, & td:last-of-type': { pr: 2 },

  // The column names are labels and stand back from the rows: small, medium weight, quiet.
  '& thead th': {
    height: 36,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    color: tableText.tertiary,
    borderColor: 'divider',
  },
  '& .MuiTableSortLabel-root:hover': { color: tableText.secondary },
  '& .MuiTableSortLabel-root.Mui-active': { color: tableText.primary },
  '& .MuiTableSortLabel-icon': { fontSize: 14 },

  '& tbody td': {
    height: rowHeight,
    fontSize: 13,
    color: tableText.secondary,
  },
  // A checkbox not ticked, and a row's tools, stand back until the row is pointed at.
  '& .MuiCheckbox-root:not(.Mui-checked):not(.MuiCheckbox-indeterminate)': { color: tableText.tertiary },
  '& tbody td .MuiIconButton-root': { color: tableText.tertiary },
  '& tbody tr.dt-row:hover td .MuiIconButton-root, & tbody td .MuiIconButton-root:focus-visible': {
    color: tableText.primary,
  },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
  '& tbody tr.dt-row:hover td': { backgroundColor: hoverSurface },
  '& tbody tr.dt-selected td': { backgroundColor: selectedSurface },
  '& tbody tr.dt-selected:hover td': { backgroundColor: selectedHoverSurface },
  '& tbody tr.dt-selected td:first-of-type': { boxShadow: `inset 2px 0 0 ${brand[400]}` },

  '& th.dt-select, & td.dt-select': { width: selectColumnWidth, pl: 1, pr: 0 },
  '& .dt-end': { textAlign: 'end' },
  // Not on a phone: the row's name and its actions together are wider than the card there, and
  // stood over every other column.
  '& .dt-sticky-start': { position: { xs: 'static', sm: 'sticky' }, zIndex: 1 },
  '& .dt-sticky-end': { position: { xs: 'static', sm: 'sticky' }, right: 0, zIndex: 1 },
  // What does not fit a cell ends in an ellipsis.
  '& .dt-cell': { overflow: 'hidden', textOverflow: 'ellipsis' },
  // A muted row says what it says in the quietest shade and keeps the colours of its marks, which
  // fading it would take below what can be read.
  '& tbody tr.dt-muted td': { color: tableText.tertiary },
};

/** A thin bar over the column names while the rows are read again under them. */
export const progressStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 2,
  zIndex: 4,
};

/** A row of its own, as tall as a few rows, saying why there are none. */
export const messageCellStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  height: 180,
  whiteSpace: 'normal',
};

/** The range at the start; the page size, in a well like the search's, and the steps at the end. */
export const paginationStyles = {
  borderTop: 1,
  borderColor: rowLine,
  '& .MuiTablePagination-toolbar': { minHeight: 48, px: 2 },
  '& .MuiTablePagination-spacer': { display: 'none' },
  '& .MuiTablePagination-displayedRows': { order: -1, mr: 'auto', pr: 1.5 },
  // On a phone the bar has no room for the page size's label; the field says it by its numbers.
  '& .MuiTablePagination-selectLabel': { display: { xs: 'none', sm: 'block' } },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12, color: tableText.tertiary },
  '& .MuiTablePagination-input': {
    height: 28,
    ml: 1,
    mr: 2,
    fontSize: 12,
    color: tableText.secondary,
    border: 1,
    borderColor: fieldEdge,
    borderRadius: '6px',
    backgroundColor: segmentWell,
  },
  '& .MuiTablePagination-actions': { display: 'flex', gap: 0.5, ml: 0 },
  '& .MuiTablePagination-actions .MuiIconButton-root': { width: 28, height: 28, color: tableText.secondary },
  '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': { color: tableText.tertiary, opacity: 0.4 },
};

/**
 * Wider than 684px the bar is one line, the search at its start and the segments at its end; at
 * 684px and under, a phone's or a narrow tablet's, the search stands over the segments.
 */
const wideBar = '@media (min-width: 685px)';

/** The bar across the top of a table's card, and the bar for what is picked in its place. */
export const barStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1,
  minHeight: 52,
  px: 2,
  // Room over and under the two lines on a narrow screen.
  py: 1.25,
  [wideBar]: { py: 0 },
  borderBottom: 1,
  borderColor: 'divider',
  fontSize: 13,
  '& > .MuiIconButton-root': { color: tableText.secondary },
};

/**
 * The segments are one control of buttons side by side in a well, the one on raised out of it.
 * The theme's toggle buttons are large and cast a shadow; these are as tall as the bar's tools.
 */
export const segmentedStyles = {
  // On a narrow screen they share the line under the search, each as wide as the others; on a
  // wide one they stand at the end of the bar.
  flexGrow: 1,
  [wideBar]: { flexGrow: 0, ml: 'auto' },
  height: 32,
  p: '2px',
  gap: '2px',
  borderRadius: '8px',
  border: 1,
  borderColor: segmentRaised,
  backgroundColor: segmentWell,
  boxShadow: 'none',
  '& .MuiToggleButton-root.MuiToggleButtonGroup-grouped': {
    flex: 1,
    [wideBar]: { flex: 'none' },
    height: 26,
    m: 0,
    px: 1.25,
    gap: 0.75,
    border: 0,
    borderRadius: '6px',
    boxShadow: 'none',
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
    color: tableText.secondary,
    '&:hover': { color: tableText.primary, backgroundColor: 'transparent' },
    '&.Mui-selected, &.Mui-selected:hover': { color: tableText.primary, backgroundColor: segmentRaised },
  },
};

/** The table's search: a field in the bar as tall as its tools, sunk like the segments' well. */
export const searchStyles = {
  height: 32,
  // A line of its own on a narrow screen.
  width: '100%',
  [wideBar]: { width: 280 },
  maxWidth: '100%',
  pl: 1.25,
  pr: 0.5,
  gap: 1,
  borderRadius: '8px',
  border: 1,
  borderColor: fieldEdge,
  backgroundColor: segmentWell,
  fontSize: 13,
  color: tableText.primary,
  transition: 'border-color 120ms ease-in',
  '&:hover': { borderColor: fieldEdgeHover },
  '&.Mui-focused': { borderColor: brand[400], boxShadow: `0 0 0 3px ${alpha(brand[400], 0.25)}` },
  '& .MuiInputBase-input::placeholder': { color: tableText.tertiary, opacity: 1 },
  '& .dt-search-icon': { fontSize: 16, color: tableText.tertiary },
  '& .MuiIconButton-root': { p: 0.5, color: tableText.tertiary, '&:hover': { color: tableText.primary } },
};

/**
 * What can be done to the rows picked: one quiet button for each, alike. A dangerous one says so
 * in the colour of its words alone; its dialog is where the danger is asked about.
 */
export const bulkActionStyles = {
  height: 32,
  px: 1.25,
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: 13,
  fontWeight: 500,
  color: tableText.primary,
  backgroundColor: segmentRaised,
  '&:hover': { backgroundColor: raisedHover },
  '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
  '& .MuiButton-startIcon > *': { fontSize: 16 },
};

export const bulkDangerStyles = {
  color: red[300],
  '&:hover': { backgroundColor: alpha(red[300], 0.14) },
};

/** How many rows a segment holds, beside its name: plain, and quieter than the name. */
export const countStyles = {
  fontSize: 12,
  fontWeight: 500,
  color: tableText.tertiary,
  '.Mui-selected &': { color: tableText.secondary },
};

/**
 * What a badge says by its colour: `neutral` a state that asks for nothing, `info` a mark of what
 * a thing is (an admin, a tie to the provider), `success` what runs by itself (an account the
 * provider manages), `warning` what was switched off (single sign-on for an account), `danger`
 * what keeps a thing from use (a suspended account).
 */
export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export const toneColors: Record<BadgeTone, string> = {
  neutral: gray[400],
  info: brand[300],
  success: green[400],
  warning: orange[300],
  danger: red[300],
};

/** The colours of a filled badge. */
export const filledBadgeColors = (tone: BadgeTone) => ({
  color: toneColors[tone],
  backgroundColor: `color-mix(in srgb, ${toneColors[tone]} 16%, transparent)`,
});
